import { Injectable, NgZone } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';
import { Error } from 'tslint/lib/error';
import { SnackbarService } from '../services/snackbar.service';
import * as appActions from "./actions";
import { AppServices } from './services';

@Injectable()
export class AppEffects {
  constructor(private actions$: Actions, private appServices: AppServices, private snack: SnackbarService, private ngZone: NgZone) { }

  loginEffect = createEffect(() => this.actions$.pipe(
    ofType(appActions.login),
    concatMap(action =>
      from(this.appServices.login(action.username, action.password)).pipe(
        map(data => appActions.loginSuccess({ credential: data })),
        catchError((error: Error) => of(appActions.loginError({ error })))
      ))
  ));

  loadCurrentUserEffect = createEffect(() => this.actions$.pipe(
    ofType(appActions.loadCurrentUser),
    concatMap(action =>
      from(this.appServices.loadCurrentUser()).pipe(
        map(data => appActions.loadCurrentUserSuccess({ user: data })),
        catchError((error: Error) => of(appActions.loadCurrentUserError({ error })))
      ))
  ));

  loadCurrentUserDataEffect = createEffect(() => this.actions$.pipe(
    ofType(appActions.loadCurrentUserData),
    concatMap(action =>
      from(this.appServices.loadCurrentUserData(action.id)).pipe(
        map(data => appActions.loadCurrentUserDataSuccess({ userData: data })),
        catchError((error: Error) => of(appActions.loadCurrentUserDataError({ error })))
      ))
  ));

  loadTempBrandOwnerDataEffect = createEffect(() => this.actions$.pipe(
    ofType(appActions.loadTempBrandOwnerData),
    concatMap(action =>
      from(this.appServices.loadTempBrandOwnerData(action.id)).pipe(
        map(data => appActions.loadTempBrandOwnerDataSuccess({ brandOwnerData: data })),
        catchError((error: Error) => of(appActions.loadTempBrandOwnerDataError({ error })))
      ))
  ));

  loadCurrentUserSuccessEffect = createEffect(() => this.actions$.pipe(
    ofType(appActions.loadCurrentUserSuccess),
    concatMap(action =>
      from(this.appServices.loadCurrentUserData(action.user.user.uid)).pipe(
        map(data => this.ngZone.run(() =>appActions.loadCurrentUserDataSuccess({ userData: data }))),
        catchError((error: Error) => of(appActions.loadCurrentUserDataError({ error })))
      ))
  ));

  loginSuccessEffect = createEffect(() => this.actions$.pipe(
    ofType(appActions.loginSuccess),
    concatMap(action =>
      from(this.appServices.loadCurrentUserData(action.credential.user.uid)).pipe(
        map(data => this.ngZone.run(() =>appActions.loadCurrentUserDataSuccess({ userData: data }))),
        catchError((error: Error) => of(appActions.loadCurrentUserDataError({ error })))
      ))
  ));

  loadCurrentBrandDataEffect = createEffect(() => this.actions$.pipe(
    ofType(appActions.loadCurrentBrandData),
    concatMap(action =>
      from(this.appServices.loadCurrentBrandData(action.id)).pipe(
        map(data => appActions.loadCurrentBrandDataSuccess({ brandData: data })),
        catchError((error: Error) => of(appActions.loadCurrentBrandDataError({ error })))
      ))
  ));

  loadTempBrandDataEffect = createEffect(() => this.actions$.pipe(
    ofType(appActions.loadTempBrandData),
    concatMap(action =>
      from(this.appServices.loadCurrentBrandData(action.id)).pipe(
        map(data => appActions.loadTempBrandDataSuccess({ brandData: data })),
        catchError((error: Error) => of(appActions.loadTempBrandDataError({ error })))
      ))
  ));

  loadAllBrandsData = createEffect(() => this.actions$.pipe(
    ofType(appActions.loadAllBrandsData),
    concatMap(action =>
      from(this.appServices.loadAllBrandData()).pipe(
        map(data => appActions.loadAllBrandsDataSuccess({ brandsData: data })),
        catchError((error: Error) => of(appActions.loadAllBrandsDataError({ error })))
      ))
  ));

  createBrandOwnerEffect = createEffect(() => this.actions$.pipe(
    ofType(appActions.createBrandOwnerData),
    concatMap(action =>
      from(this.appServices.createBrandOwner(action.brandOwner)).pipe(
        map(data => appActions.createBrandOwnerDataSuccess({ brandOwner: data })),
        catchError((error: Error) => of(appActions.createBrandOwnerDataError({ error })))
      ))
  ));

  createBrandOwnerFireCredEffect = createEffect(() => this.actions$.pipe(
    ofType(appActions.createBrandOwnerFirebaseAuth),
    concatMap(action =>
      from(this.appServices.createBrandOwnerFirebaseAuth(action.brandOwner, action.password)).pipe(
        map(data => appActions.createBrandOwnerFirebaseAuthSuccess({ cred: data })),
        catchError((error: Error) => of(appActions.createBrandOwnerFirebaseAuthError({ error })))
      ))
  ));

  createBrandEffect = createEffect(() => this.actions$.pipe(
    ofType(appActions.createBrandData),
    concatMap(action =>
      from(this.appServices.createBrand(action.brand)).pipe(
        map(data => appActions.createBrandDataSuccess({ brand: data })),
        catchError((error: Error) => of(appActions.createBrandDataError({ error })))
      ))
  ));

  updateBrandOwnerEffect = createEffect(() => this.actions$.pipe(
    ofType(appActions.updateBrandOwnerData),
    concatMap(action =>
      from(this.appServices.updateBrandOwner(action.brandOwner)).pipe(
        map(data => appActions.updateBrandOwnerDataSuccess({ brandOwner: data })),
        catchError((error: Error) => of(appActions.updateBrandOwnerDataError({ error })))
      ))
  ));

  updateBrandEffect = createEffect(() => this.actions$.pipe(
    ofType(appActions.updateBrandData),
    concatMap(action =>
      from(this.appServices.updateBrand(action.brand)).pipe(
        map(data => appActions.updateBrandDataSuccess()),
        catchError((error: Error) => of(appActions.updateBrandDataError({ error })))
      ))
  ));

  updateTempBrandEffect = createEffect(() => this.actions$.pipe(
    ofType(appActions.updateTempBrandData),
    concatMap(action =>
      from(this.appServices.updateBrand(action.brand)).pipe(
        map(data => appActions.updateTempBrandDataSuccess()),
        catchError((error: Error) => of(appActions.updateTempBrandDataError({ error })))
      ))
  ));


}